import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Library, Pencil, AlertCircle, RotateCcw, Plus, Building2, Users, BarChart3 } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { AdminNav } from "@/components/neusim/AdminNav";
import { apiCall, ApiError } from "@/lib/api";

interface SimRow {
  id: string;
  simCode: string;
  title: string;
  description: string | null;
  audience: string | null;
  difficulty: string | null;
  projectName: string | null;
  competencyName: string | null;
  createdAt: string;
  nodeCount: number;
}

function difficultyToken(difficulty: string | null): { color: string; bg: string } {
  switch ((difficulty ?? "").toLowerCase()) {
    case "high": case "hard":
      return { color: "#F87272", bg: "rgba(248,114,114,0.12)" };
    case "medium":
      return { color: "#F59E0B", bg: "rgba(245,158,11,0.12)" };
    case "low": case "easy":
      return { color: "#10B981", bg: "rgba(16,185,129,0.12)" };
    default:
      return { color: "#A78BFA", bg: "rgba(167,139,250,0.10)" };
  }
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * AdminSimLibraryPage — /admin/sims — super_admin-only list of every sim in
 * the system. Click a row → /admin/sims/:id to edit. "New simulation" goes to
 * the existing AI/Excel builder at /admin/sims/new.
 */
export function AdminSimLibraryPage(): JSX.Element {
  const navigate = useNavigate();
  const [sims, setSims] = useState<SimRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall<{ sims: SimRow[] }>("/libraryList");
      setSims(res.sims);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load the Sim Library";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  return (
    <ScreenShell>
      <SimHeader decision={sims.length} xp={0} />
      <main className="mx-auto max-w-6xl px-4 pb-20 md:px-8">
        <div className="mb-5"><AdminNav /></div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div>
            <div className="flex items-center gap-2 text-fuchsia-300">
              <Library className="h-4 w-4" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Sim Library</span>
            </div>
            <h1 className="mt-2 text-[28px] font-bold leading-tight">
              <GradientText>Every simulation in the system</GradientText>
            </h1>
            <p className="mt-1 text-sm text-white/55">Click a row to edit. New sims author via AI Builder or Excel Upload.</p>
          </div>
        </motion.div>

        {loading && (
          <div className="flex min-h-[30vh] flex-col items-center justify-center text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} className="h-10 w-10 rounded-full border-2 border-white/10 border-t-fuchsia-400" />
            <p className="mt-4 text-sm text-white/55">Loading sims…</p>
          </div>
        )}

        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-8 max-w-lg p-8 text-center">
            <AlertCircle className="mx-auto h-9 w-9 text-[#f87272]" />
            <p className="mt-3 text-sm text-white/70">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Try again
            </button>
          </GlassCard>
        )}

        {!loading && error === null && sims.length === 0 && (
          <GlassCard className="mt-8 p-10 text-center">
            <p className="text-sm text-white/55">No simulations yet. Use the AI Builder or Excel Upload to author one.</p>
          </GlassCard>
        )}

        {!loading && error === null && sims.length > 0 && (
          <GlassCard className="mt-6 p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03] text-[10px] uppercase tracking-[0.14em] text-white/45">
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Title</th>
                    <th className="px-4 py-3 text-left">Audience</th>
                    <th className="px-4 py-3 text-left">Difficulty</th>
                    <th className="px-4 py-3 text-right">Decisions</th>
                    <th className="px-4 py-3 text-left">Project</th>
                    <th className="px-4 py-3 text-left">Created</th>
                    <th className="px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {sims.map((s) => {
                    const d = difficultyToken(s.difficulty);
                    return (
                      <tr
                        key={s.id}
                        onClick={() => navigate(`/admin/sims/${s.id}`)}
                        className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.04]"
                      >
                        <td className="px-4 py-3 font-mono text-[12px] text-white/70">{s.simCode}</td>
                        <td className="px-4 py-3 font-medium text-white">{s.title}</td>
                        <td className="px-4 py-3 text-white/65">{s.audience ?? "—"}</td>
                        <td className="px-4 py-3">
                          {s.difficulty !== null ? (
                            <span
                              className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]"
                              style={{ background: d.bg, border: `1px solid ${d.color}66`, color: d.color }}
                            >
                              {s.difficulty}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-white/85">{s.nodeCount}</td>
                        <td className="px-4 py-3 text-white/65">{s.projectName ?? "—"}</td>
                        <td className="px-4 py-3 text-[12px] text-white/45">{fmtDate(s.createdAt)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center gap-1 text-[11px] text-fuchsia-300 group-hover:text-fuchsia-200">
                            <Pencil className="h-3 w-3" /> Edit
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </main>
    </ScreenShell>
  );
}
